/**
 * @fileoverview Modal de Zona Peligrosa y Reinicio Total para la dinámica 'Rasca y Gana - Optimo Ópticas'.
 * Protege la integridad de la base de datos solicitando la contraseña maestra oficial
 * para archivar y purgar de forma definitiva los participantes, métricas y contadores de la jornada.
 * 
 * @module components/Admin/Overlays/OptimoOpticas/OptimoOpticasDangerModal
 */

import React, { useState, useEffect } from 'react';
import { useFloatMessage, MESSAGE_TYPE } from '../../../Providers/FloatMessage';
import { ResetDynamic, GetAdminOperatorName } from '../../../../utilities/BL/OptimoOpticas';

/**
 * Componente modal de confirmación segura con contraseña para reiniciar la dinámica.
 * 
 * @function OptimoOpticasDangerModal
 * @param {Object} props
 * @param {boolean} props.isOpen - Visibilidad del modal.
 * @param {Function} props.onClose - Callback al cerrar.
 * @param {Function} props.onResetSuccess - Callback ejecutado tras el reinicio exitoso.
 * @returns {React.ReactElement|null}
 */
export const OptimoOpticasDangerModal = ({
    isOpen,
    onClose,
    onResetSuccess
}) => {
    const { showMessage } = useFloatMessage();
    const [passwordInput, setPasswordInput] = useState('');
    const [operatorName, setOperatorName] = useState('Administrador');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showPassword, setShowPassword] = useState(false);

    useEffect(() => {
        if (isOpen) {
            setPasswordInput('');
            setShowPassword(false);
            const op = GetAdminOperatorName();
            setOperatorName(op || 'Administrador');
        }
    }, [isOpen]);

    if (!isOpen) return null;

    const handleSubmit = async (e) => {
        e.preventDefault();
        const trimmed = passwordInput.trim();
        if (!trimmed) {
            showMessage({
                type: MESSAGE_TYPE.BAD,
                text: 'Ingresa la contraseña maestra para confirmar esta acción crítica.'
            });
            return;
        }

        // Validación de contraseña maestra oficial ADSL o contraseña corta autorizada
        const validMasterPasswords = ['evitarborrarregistros456852'];
        if (!validMasterPasswords.includes(trimmed)) {
            showMessage({
                type: MESSAGE_TYPE.BAD,
                text: 'Contraseña maestra incorrecta. No tienes autorización para reiniciar la dinámica.'
            });
            return;
        }

        setIsSubmitting(true);
        try {
            const res = await ResetDynamic();
            if (res && res.success !== false) {
                showMessage({
                    type: MESSAGE_TYPE.SUCCESS,
                    text: '¡La dinámica de Optimo Ópticas ha sido reiniciada con éxito para el nuevo partido!'
                });
                if (onResetSuccess) onResetSuccess();
                onClose();
            } else {
                showMessage({
                    type: MESSAGE_TYPE.BAD,
                    text: res?.message || 'Error al reiniciar la dinámica en el servidor.'
                });
            }
        } catch (error) {
            showMessage({
                type: MESSAGE_TYPE.BAD,
                text: error.message || 'Error de conexión durante el reinicio total.'
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.75)', zIndex: 1060 }}>
            <div className="modal-dialog modal-dialog-centered">
                <div className="modal-content border-0 shadow-lg rounded-4 overflow-hidden">
                    {/* Header */}
                    <div className="modal-header bg-danger text-white border-0 py-3">
                        <h5 className="modal-title fw-bold d-flex align-items-center gap-2">
                            <i className="bi bi-exclamation-triangle-fill fs-4"></i>
                            Zona Peligrosa: Reinicio de Optimo Ópticas
                        </h5>
                        <button type="button" className="btn-close btn-close-white" onClick={onClose} aria-label="Close"></button>
                    </div>

                    {/* Body */}
                    <form onSubmit={handleSubmit}>
                        <div className="modal-body p-4 bg-light">
                            <div className="alert alert-danger py-3 px-3 rounded-3 mb-3 border-danger">
                                <h6 className="fw-bold text-danger mb-1 d-flex align-items-center gap-2">
                                    <i className="bi bi-shield-fill-x fs-5"></i>
                                    ¡Acción Crítica e Irreversible!
                                </h6>
                                <p className="small mb-0 text-dark">
                                    Esta operación purgará permanentemente a <strong>todos los participantes registrados</strong>,
                                    pondrá en cero el embudo de telemetría y reiniciará el stock de cupones para el nuevo partido.
                                </p>
                            </div>

                            <div className="mb-3">
                                <label className="form-label fw-bold text-dark small">
                                    Contraseña Maestra Oficial *
                                </label>
                                <div className="input-group">
                                    <input
                                        type={showPassword ? 'text' : 'password'}
                                        className="form-control"
                                        placeholder="Ingresa la contraseña maestra..."
                                        value={passwordInput}
                                        onChange={(e) => setPasswordInput(e.target.value)}
                                        required
                                        autoFocus
                                    />
                                    <button
                                        type="button"
                                        className="btn btn-outline-secondary"
                                        onClick={() => setShowPassword(!showPassword)}
                                        tabIndex="-1"
                                    >
                                        <i className={`bi ${showPassword ? 'bi-eye-slash-fill' : 'bi-eye-fill'}`}></i>
                                    </button>
                                </div>
                                <small className="text-muted" style={{ fontSize: '0.75rem' }}>
                                    Protección administrativa contra borrado accidental de registros.
                                </small>
                            </div>
                        </div>

                        {/* Footer */}
                        <div className="modal-footer bg-light border-top">
                            <button type="button" className="btn btn-outline-secondary fw-bold" onClick={onClose} disabled={isSubmitting}>
                                Cancelar
                            </button>
                            <button
                                type="submit"
                                className="btn btn-danger fw-bold d-flex align-items-center gap-2"
                                disabled={isSubmitting}
                            >
                                {isSubmitting ? (
                                    <>
                                        <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                                        Reiniciando...
                                    </>
                                ) : (
                                    <>
                                        <i className="bi bi-trash-fill"></i>
                                        Confirmar y Reiniciar Dinámica
                                    </>
                                )}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};
